import React from 'react';
import { Helmet } from 'react-helmet-async';
import { clientContent } from '../../client/content';
import Section from '../../components/Section';
import CtaBlock from '../../components/CtaBlock';
import styles from './About.module.scss';

const About: React.FC = () => {
  const { title, description, canonical } = clientContent.seo.about;
  const { title: pageTitle, subtitle, whoYouAre, whyCreated, philosophy, howWeWork } = clientContent.about;

  return (
    <div className={styles.about} data-testid="about-page">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
      </Helmet>

      {/* Page Header */}
      <section className="pageHeader">
        <div className="container">
          <span className="eyebrow">About Us</span>
          <h1>{pageTitle}</h1>
          <p>{subtitle}</p>
        </div>
      </section>

      {/* 1. Who You Are Section */}
      <Section background="default">
        <div className={styles.whoSection}>
          <h2>{whoYouAre.heading}</h2>
          <div className={styles.founderCard}>
            <div className={styles.founderMeta}>
              <div className={styles.avatarIcon}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
              </div>
              <h3>{whoYouAre.name}</h3>
              <span className={styles.role}>{whoYouAre.role}</span>
              <p className={styles.experience}>{whoYouAre.experience}</p>
            </div>
            <div className={styles.bioParagraphs}>
              {whoYouAre.bioParagraphs.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* 2. Why You Created It Section */}
      <Section background="surface">
        <div className={styles.whySection}>
          <h2>{whyCreated.heading}</h2>
          <div className={styles.whyCard}>
            <p className={styles.whyText}>{whyCreated.description}</p>
          </div>
        </div>
      </Section>

      {/* 3. Your Philosophy Section */}
      <Section background="default">
        <div className={styles.philosophySection}>
          <h2>{philosophy.heading}</h2>
          <div className={styles.philosophyGrid}>
            {philosophy.items.map((item, index) => (
              <div key={index} className={styles.philosophyCard}>
                <div className={styles.icon}>
                  {index === 0 && (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>
                  )}
                  {index === 1 && (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                  )}
                  {index === 2 && (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"></circle><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line></svg>
                  )}
                  {index === 3 && (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                  )}
                  {index === 4 && (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
                  )}
                  {index === 5 && (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                  )}
                </div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* 4. How You Work Section */}
      <Section background="surface">
        <div className={styles.howSection}>
          <h2>{howWeWork.heading}</h2>
          <div className={styles.stepsGrid}>
            {howWeWork.steps.map((s, index) => (
              <div key={index} className={styles.stepCard}>
                <span className={styles.stepNumber}>{s.step}</span>
                <h3>{s.title}</h3>
                <p>{s.description}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* 5. CTA Section */}
      <Section background="default">
        <CtaBlock
          title="Think we could help?"
          subtitle="Get in touch to discuss your website and let us take care of the technical side."
          buttonText="Get in Touch"
          buttonPath="/contact"
          secondaryButtonText="Explore Concepts"
          secondaryButtonPath="/concepts"
        />
      </Section>
    </div>
  );
};

export default About;
